// Tier 8 survey targeting + auto-events for the JS engine.
//
// Port of core/src/surveys.rs. Same reason as feature-flags.ts: the Rust core
// cannot execute on React Native, and until this landed `getActiveSurveys()`
// returned `[]` for every survey and the three auto-events were never emitted,
// so surveys simply did not exist on RN.
//
// Normalization matters as much as evaluation here. Rust serializes
// `SurveyDefinition` with serde defaults expanded — no `skip_serializing_if`
// anywhere — so `surveyDefinitionsJson()` on the WASM path emits every field
// including the nulls. `normalizeDefinition` reproduces that shape exactly;
// without it, wrappers that read the JSON would see two different survey
// objects depending on which engine happened to be running.
import type {
  SurveyDefinition,
  SurveyDisplay,
  SurveyQuestion,
  SurveyResponse,
  SurveyTargeting,
  SurveyTargetingContext,
  SurveyType
} from './types.js';

/** The three auto-events (surveys.rs:379-451). Names are wire contract. */
export type SurveyAutoEventName = 'survey shown' | 'survey dismissed' | 'survey sent';

export interface SurveyAutoEvent {
  eventName: SurveyAutoEventName;
  properties: Record<string, unknown>;
}

/** Survey auto-events, excluded from the event-history counter to avoid a loop. */
export const SURVEY_AUTO_EVENT_NAMES: readonly SurveyAutoEventName[] = [
  'survey shown',
  'survey dismissed',
  'survey sent'
];

const VALID_SURVEY_TYPES: readonly SurveyType[] = [
  'nps',
  'csat',
  'ces',
  'multiple_choice',
  'rating',
  'open_text',
  'link'
];

type CountOp = 'gt' | 'lt' | 'eq' | 'gte' | 'lte';

// ── Normalization ────────────────────────────────────────────────────────
//
// SERDE-FAITHFUL, FIELD BY FIELD. Rust's `SurveyDefinition` is a typed struct,
// and `#[serde(default)]` only supplies a value when a field is ABSENT — a
// field that is PRESENT with the wrong type is a hard deserialization error
// that takes the whole survey with it (`remote_config.rs:211-220` then drops
// it). So coercing here (`d.dismissable !== false`), filtering here
// (`options.filter(isString)`), or defaulting a required field here
// (`max_shows ?? 0`) all produce a survey that exists on this engine and does
// not exist on the Rust one — `knownSurveyIds()`, `surveyDefinitionsJson()`
// and eligibility all disagree. Every helper below therefore REJECTS rather
// than repairs.

/** Thrown by the strict accessors; caught at the definition boundary. */
class InvalidSurveyConfig extends Error {}

const invalid = (): never => {
  throw new InvalidSurveyConfig();
};

// NULL IS A VALUE, NOT AN ABSENCE. This is the distinction the helpers below
// are named for, and getting it wrong is invisible without a dual-engine test.
//
// `#[serde(default)]` fires only when a key is MISSING. An explicit `null` is
// a value serde must deserialize as the declared type — and `null` is not a
// `bool`, a `u32`, a `Vec<T>`, a `String` or an enum variant, so the whole
// definition fails and `remote_config.rs:211-220` drops the survey. `null` is
// valid for exactly one shape: `Option<T>`, where it means `None`.
//
// Verified field-by-field against the real WASM binary, not inferred: the
// `Option` fields (targeting, display, url_match, feature_flag, event_history,
// frequency_cap, scale_min/max, link_url/label, delay_ms, position) KEEP the
// survey on null; every other field DROPS it. Each accessor below is named
// `opt*` (nullable) or `req*` / `def*` (not) to keep that split legible.

/** Missing key. JSON.parse never yields `undefined`, so this is exactly "absent". */
const isMissing = (v: unknown): boolean => v === undefined;

/** Absent OR null — correct for `Option<T>` fields ONLY. */
const isNone = (v: unknown): boolean => v === undefined || v === null;

/** `Option<String>` — null is `None`. */
function optString(v: unknown): string | undefined {
  if (isNone(v)) return undefined;
  return typeof v === 'string' ? v : invalid();
}

/** A required `String` field (no serde default). `null` is not a String. */
function reqString(v: unknown): string {
  return typeof v === 'string' ? v : invalid();
}

/** `Option<u64>` / `Option<u32>` — null is `None`. */
function optUint(v: unknown): number | undefined {
  if (isNone(v)) return undefined;
  return typeof v === 'number' && Number.isInteger(v) && v >= 0 ? v : invalid();
}

/** `Option<i32>` — `scale_min` / `scale_max` may be negative. */
function optInt(v: unknown): number | undefined {
  if (isNone(v)) return undefined;
  return typeof v === 'number' && Number.isInteger(v) ? v : invalid();
}

/** A bare `u32` with a serde default: MISSING takes the default; null errors. */
function defUint(v: unknown, fallback: number): number {
  if (isMissing(v)) return fallback;
  return typeof v === 'number' && Number.isInteger(v) && v >= 0 ? v : invalid();
}

/** A bare `u32` with NO default — required, and null is not a number. */
function reqUint(v: unknown): number {
  return typeof v === 'number' && Number.isInteger(v) && v >= 0 ? v : invalid();
}

/** A bare `bool` with a serde default: MISSING takes the default; null errors. */
function defBool(v: unknown, fallback: boolean): boolean {
  if (isMissing(v)) return fallback;
  return typeof v === 'boolean' ? v : invalid();
}

/** `Vec<String>` with `#[serde(default)]`: MISSING is `[]`; null errors. */
function defStringArray(v: unknown): string[] {
  if (isMissing(v)) return [];
  if (!Array.isArray(v)) return invalid();
  return v.every((s) => typeof s === 'string') ? (v as string[]) : invalid();
}

/** `Vec<T>` with `#[serde(default)]`: MISSING is `[]`; null errors. */
function defArray(v: unknown): unknown[] {
  if (isMissing(v)) return [];
  return Array.isArray(v) ? v : invalid();
}

/** A bare enum field with a serde default: MISSING takes the default; null errors. */
function defEnum<T extends string>(v: unknown, variants: readonly T[], fallback: T): T {
  if (isMissing(v)) return fallback;
  return typeof v === 'string' && (variants as readonly string[]).includes(v)
    ? (v as T)
    : invalid();
}

/** A bare enum field with NO default — required, and null is not a variant. */
function reqEnum<T extends string>(v: unknown, variants: readonly T[]): T {
  return typeof v === 'string' && (variants as readonly string[]).includes(v)
    ? (v as T)
    : invalid();
}

/** `Option<enum>` — null is `None`. */
function optEnum<T extends string>(v: unknown, variants: readonly T[]): T | undefined {
  if (isNone(v)) return undefined;
  return typeof v === 'string' && (variants as readonly string[]).includes(v)
    ? (v as T)
    : invalid();
}

/** `Option<Struct>` — null is `None`. An array or scalar errors. */
function optObject(v: unknown): Record<string, unknown> | undefined {
  if (isNone(v)) return undefined;
  return typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : invalid();
}

const COUNT_OPS: readonly CountOp[] = ['gt', 'lt', 'eq', 'gte', 'lte'];
const SURVEY_POSITIONS = ['bottom-right', 'bottom-center', 'center', 'top-center'] as const;

function normalizeQuestion(raw: unknown): SurveyQuestion {
  // `questions` is a `Vec<SurveyQuestion>`; a null element is not a struct.
  if (raw === null) invalid();
  const q = optObject(raw) ?? invalid();
  const out: SurveyQuestion = {
    // `id`, `text` and `type` have no serde default.
    id: reqString(q.id),
    text: reqString(q.text),
    type: reqEnum(q.type, VALID_SURVEY_TYPES),
    // `Vec<String>` / `bool`, both `#[serde(default)]` — not Options.
    options: defStringArray(q.options),
    required: defBool(q.required, false)
  };
  // `exactOptionalPropertyTypes` is on: an optional key must be absent rather
  // than present-and-undefined, so each is added only when it has a value.
  const scaleMin = optInt(q.scale_min);
  if (scaleMin !== undefined) out.scale_min = scaleMin;
  const scaleMax = optInt(q.scale_max);
  if (scaleMax !== undefined) out.scale_max = scaleMax;
  const linkUrl = optString(q.link_url);
  if (linkUrl !== undefined) out.link_url = linkUrl;
  const linkLabel = optString(q.link_label);
  if (linkLabel !== undefined) out.link_label = linkLabel;
  return out;
}

/**
 * `surveys::Condition` (surveys.rs:217-244) — the Tier 4 shape MINUS
 * percentage_rollout and cohort, since a survey condition set is AND-combined
 * and carries no distinct_id to bucket on.
 */
const SURVEY_CONDITION_TYPES = new Set([
  'property_equals',
  'property_in',
  'property_contains',
  'property_regex',
  'property_greater_than',
  'property_less_than'
]);

/**
 * Validate one targeting condition the way serde does. Rust's enum has no
 * fallback variant, so one bad condition fails the whole `SurveyDefinition`
 * and `remote_config.rs:211-220` drops that survey. Failing only the condition
 * here would leave the survey listed by `knownSurveyIds()` on one engine and
 * absent on the other.
 */
function isValidSurveyCondition(raw: unknown): boolean {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return false;
  const c = raw as Record<string, unknown>;
  if (typeof c.type !== 'string' || !SURVEY_CONDITION_TYPES.has(c.type)) return false;
  if (typeof c.property !== 'string') return false;
  switch (c.type) {
    case 'property_in':
      return Array.isArray(c.values);
    case 'property_contains':
      return typeof c.value === 'string';
    case 'property_regex':
      return typeof c.pattern === 'string';
    case 'property_greater_than':
    case 'property_less_than':
      return typeof c.value === 'number';
    default: // property_equals — untyped JSON value, but required.
      return 'value' in c;
  }
}

function normalizeTargeting(raw: unknown): SurveyTargeting | undefined {
  const t = optObject(raw);
  if (t === undefined) return undefined;

  // `Vec<Condition>` with `#[serde(default)]` — MISSING is `[]`, null is a
  // type error that drops the survey.
  const rawConditions = defArray(t.person_properties);
  if (!rawConditions.every(isValidSurveyCondition)) invalid();

  const out: SurveyTargeting = {
    person_properties: rawConditions as Array<Record<string, unknown>>
  };
  const urlMatch = optString(t.url_match);
  if (urlMatch !== undefined) out.url_match = urlMatch;
  const featureFlag = optString(t.feature_flag);
  if (featureFlag !== undefined) out.feature_flag = featureFlag;

  const eh = optObject(t.event_history);
  if (eh !== undefined) {
    // `event_name` is required; days/count_op/count are bare types with serde
    // defaults of 30 / gte / 1 (surveys.rs:149-167) — so MISSING takes the
    // default and null is a type error.
    out.event_history = {
      event_name: reqString(eh.event_name),
      days: defUint(eh.days, 30),
      count_op: defEnum(eh.count_op, COUNT_OPS, 'gte'),
      count: defUint(eh.count, 1)
    };
  }

  const cap = optObject(t.frequency_cap);
  if (cap !== undefined) {
    // BOTH fields are required on `FrequencyCap` — no serde defaults
    // (surveys.rs:181-185). Defaulting `max_shows` to 0 would silently make
    // the survey permanently ineligible instead of dropping it.
    out.frequency_cap = { max_shows: reqUint(cap.max_shows), days: reqUint(cap.days) };
  }
  return out;
}

function normalizeDisplay(raw: unknown): SurveyDisplay | undefined {
  const d = optObject(raw);
  if (d === undefined) return undefined;
  // Bare `bool`, `#[serde(default = "default_dismissable")]` → true
  // (surveys.rs:194-200). Missing takes the default; null is a type error.
  const out: SurveyDisplay = { dismissable: defBool(d.dismissable, true) };
  const delay = optUint(d.delay_ms);
  if (delay !== undefined) out.delay_ms = delay;
  const position = optEnum(d.position, SURVEY_POSITIONS);
  if (position !== undefined) out.position = position;
  return out;
}

/**
 * Normalize one raw `/config` survey into the shape Rust's serde emits, or
 * null if it would have failed deserialization (dropped best-effort, matching
 * `remote_config.rs:211-220`).
 *
 * The `undefined` values here become `null` through `JSON.stringify`, which is
 * exactly what serde writes for `Option::None`.
 */
export function normalizeDefinition(raw: unknown): SurveyDefinition | null {
  try {
    return buildDefinition(raw);
  } catch (e) {
    // Only OUR rejection is a dropped survey; anything else is a real bug and
    // must not be swallowed as "malformed config".
    if (e instanceof InvalidSurveyConfig) return null;
    throw e;
  }
}

function buildDefinition(raw: unknown): SurveyDefinition {
  if (raw === null) invalid(); // a null array element is not a struct
  const s = optObject(raw) ?? invalid();

  // `questions` has NO serde default — absent OR null fails outright.
  const rawQuestions = s.questions;
  if (!Array.isArray(rawQuestions)) invalid();

  const out: SurveyDefinition = {
    id: reqString(s.id),
    name: reqString(s.name),
    type: reqEnum(s.type, VALID_SURVEY_TYPES),
    // Bare `bool` with a serde default — null is a type error, not "absent".
    active: defBool(s.active, true),
    // One bad question fails the whole survey — `normalizeQuestion` throws.
    questions: (rawQuestions as unknown[]).map(normalizeQuestion)
  };

  const targeting = normalizeTargeting(s.targeting);
  if (targeting !== undefined) out.targeting = targeting;
  const display = normalizeDisplay(s.display);
  if (display !== undefined) out.display = display;
  return out;
}

/** Parse the `surveys` array of a `/config` body. */
export function parseSurveyConfig(raw: unknown): SurveyDefinition[] | null {
  if (!Array.isArray(raw)) return null;
  return raw.map(normalizeDefinition).filter((d): d is SurveyDefinition => d !== null);
}

/**
 * `JSON.stringify` drops `undefined` members; serde writes `null`. Expanding
 * them keeps `surveyDefinitionsJson()` byte-comparable with the WASM path.
 */
function withExplicitNulls(def: SurveyDefinition): Record<string, unknown> {
  const question = (q: SurveyQuestion): Record<string, unknown> => ({
    id: q.id,
    text: q.text,
    type: q.type,
    options: q.options ?? [],
    scale_min: q.scale_min ?? null,
    scale_max: q.scale_max ?? null,
    required: q.required ?? false,
    link_url: q.link_url ?? null,
    link_label: q.link_label ?? null
  });
  const targeting = (t: SurveyTargeting): Record<string, unknown> => ({
    url_match: t.url_match ?? null,
    feature_flag: t.feature_flag ?? null,
    person_properties: t.person_properties ?? [],
    event_history: t.event_history ?? null,
    frequency_cap: t.frequency_cap ?? null
  });
  const display = (d: SurveyDisplay): Record<string, unknown> => ({
    delay_ms: d.delay_ms ?? null,
    position: d.position ?? null,
    dismissable: d.dismissable ?? true
  });
  return {
    id: def.id,
    name: def.name,
    type: def.type,
    active: def.active ?? true,
    questions: (def.questions ?? []).map(question),
    targeting: def.targeting ? targeting(def.targeting) : null,
    display: def.display ? display(def.display) : null
  };
}

// ── Condition evaluation ─────────────────────────────────────────────────
//
// `surveys::Condition` is the Tier 4 condition shape MINUS
// percentage_rollout and cohort (surveys.rs:217-244) — a survey condition set
// is AND-combined and carries no distinct_id, so there is nothing to bucket.

function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a === null || b === null || typeof a !== 'object' || typeof b !== 'object') return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((item, i) => deepEqual(item, b[i]));
  }
  const ao = a as Record<string, unknown>;
  const bo = b as Record<string, unknown>;
  const aKeys = Object.keys(ao);
  const bKeys = Object.keys(bo);
  return aKeys.length === bKeys.length && aKeys.every((k) => k in bo && deepEqual(ao[k], bo[k]));
}

function containsInsensitive(value: unknown, needle: string): boolean {
  return typeof value === 'string' && value.toLowerCase().includes(needle.toLowerCase());
}

/**
 * `serde_json::Value::as_f64` — numbers only, so a numeric STRING never
 * compares (surveys.rs:626-633). Deliberately stricter than the SKAN engine's
 * coercion.
 */
function asNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

/**
 * Port of `evaluate_condition` (surveys.rs:600-635). Every condition reaching
 * here already passed `isValidSurveyCondition` at parse time, so `property`,
 * `value` and `pattern` are known to be the right types.
 */
function evaluateCondition(
  condition: Record<string, unknown>,
  properties: Record<string, unknown>
): boolean {
  const property = condition.property as string;
  const actual = properties[property];
  switch (condition.type) {
    case 'property_equals':
      return property in properties ? deepEqual(actual, condition.value) : false;
    case 'property_in':
      return (
        property in properties &&
        Array.isArray(condition.values) &&
        condition.values.some((v) => deepEqual(actual, v))
      );
    case 'property_contains':
      return containsInsensitive(actual, condition.value as string);
    case 'property_regex':
      return containsInsensitive(actual, condition.pattern as string);
    case 'property_greater_than': {
      const n = asNumber(actual);
      return n !== undefined && n > (condition.value as number);
    }
    case 'property_less_than': {
      const n = asNumber(actual);
      return n !== undefined && n < (condition.value as number);
    }
    default:
      // Rust rejects unknown discriminants at parse time, dropping the whole
      // survey. Failing the condition here is the same outcome for the user:
      // a survey we do not understand never shows.
      return false;
  }
}

/** Port of `count_op_matches` (surveys.rs:590-598). */
function countOpMatches(op: CountOp, actual: number, target: number): boolean {
  switch (op) {
    case 'gt':
      return actual > target;
    case 'lt':
      return actual < target;
    case 'eq':
      return actual === target;
    case 'lte':
      return actual <= target;
    case 'gte':
    default:
      return actual >= target;
  }
}

function surveyTypeString(type: SurveyType): string {
  return type;
}

// ── Engine ───────────────────────────────────────────────────────────────

/** Port of `SurveyManager` (surveys.rs:295-507). */
export class SurveyEngine {
  private definitions = new Map<string, SurveyDefinition>();
  /** Per-survey-id ring of unix-ms show timestamps. */
  private showHistoryMap = new Map<string, number[]>();
  private eventCounts = new Map<string, number>();
  private dismissedThisSession = new Set<string>();
  private completed = new Set<string>();

  setDefinitions(defs: readonly SurveyDefinition[]): void {
    this.definitions.clear();
    for (const def of defs) this.definitions.set(def.id, def);
  }

  knownIds(): string[] {
    return [...this.definitions.keys()];
  }

  /** Sorted by id for determinism (surveys.rs:345-350). */
  definitionsList(): SurveyDefinition[] {
    return [...this.definitions.values()].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  }

  /** The serde-shaped JSON the WASM path returns from `surveyDefinitionsJson`. */
  definitionsJson(): string {
    return JSON.stringify(this.definitionsList().map(withExplicitNulls));
  }

  /** Port of `get_active_surveys` (surveys.rs:354-364). Sorted by id. */
  getActiveSurveys(ctx: SurveyTargetingContext = {}): SurveyDefinition[] {
    return this.definitionsList().filter((def) => this.isEligible(def, ctx));
  }

  /** Port of `record_show` (surveys.rs:379-400). Null for an unknown id. */
  recordShow(surveyId: string): SurveyAutoEvent | null {
    const def = this.definitions.get(surveyId);
    if (!def) return null;
    const history = this.showHistoryMap.get(surveyId) ?? [];
    history.push(Date.now());
    this.showHistoryMap.set(surveyId, history);
    return { eventName: 'survey shown', properties: this.baseProperties(def) };
  }

  /** Port of `record_dismiss` (surveys.rs:402-418). */
  recordDismiss(surveyId: string): SurveyAutoEvent | null {
    const def = this.definitions.get(surveyId);
    if (!def) return null;
    this.dismissedThisSession.add(surveyId);
    return { eventName: 'survey dismissed', properties: this.baseProperties(def) };
  }

  /** Port of `record_response` (surveys.rs:420-451). */
  recordResponse(surveyId: string, response: SurveyResponse): SurveyAutoEvent | null {
    const def = this.definitions.get(surveyId);
    if (!def) return null;
    this.completed.add(surveyId);
    const properties: Record<string, unknown> = this.baseProperties(def);
    properties.answers = response?.answers ?? [];
    if (typeof response?.shown_at === 'string') properties.shown_at = response.shown_at;
    if (typeof response?.completed_at === 'string') {
      properties.completed_at = response.completed_at;
    }
    return { eventName: 'survey sent', properties };
  }

  /** Port of `record_event` (surveys.rs:456-463) — feeds event_history targeting. */
  recordEvent(eventName: string): void {
    this.eventCounts.set(eventName, (this.eventCounts.get(eventName) ?? 0) + 1);
  }

  /** Port of `clear_session_state` (surveys.rs:492-496). */
  clearSessionState(): void {
    this.dismissedThisSession.clear();
    this.eventCounts.clear();
  }

  /**
   * Restore a persisted show-history map. All-or-nothing on purpose: Rust
   * deserializes into `HashMap<String, Vec<u64>>`, so ONE bad entry fails the
   * whole call and `seed_survey_show_history` returns an error that leaves the
   * existing history untouched (client.rs:1858-1865). Filtering the bad
   * entries out instead would silently seed a PARTIAL history — and a survey
   * whose shows went missing is a survey that re-shows past its frequency cap.
   *
   * Returns false when the input was rejected.
   */
  seedShowHistory(history: Record<string, unknown>): boolean {
    const parsed = new Map<string, number[]>();
    for (const [id, times] of Object.entries(history)) {
      if (!Array.isArray(times)) return false;
      // `u64`: non-negative integers only, as serde would enforce.
      if (!times.every((t) => typeof t === 'number' && Number.isInteger(t) && t >= 0)) {
        return false;
      }
      parsed.set(id, times as number[]);
    }
    this.showHistoryMap = parsed;
    return true;
  }

  showHistory(): Record<string, number[]> {
    const out: Record<string, number[]> = {};
    for (const [id, times] of this.showHistoryMap) out[id] = [...times];
    return out;
  }

  hasData(): boolean {
    return this.definitions.size > 0;
  }

  private baseProperties(def: SurveyDefinition): Record<string, unknown> {
    return {
      survey_id: def.id,
      survey_name: def.name,
      survey_type: surveyTypeString(def.type)
    };
  }

  /** Port of `evaluate_with_state` (surveys.rs:517-576). */
  private isEligible(def: SurveyDefinition, ctx: SurveyTargetingContext): boolean {
    if (def.active === false) return false;
    if (this.dismissedThisSession.has(def.id)) return false;
    if (this.completed.has(def.id)) return false;

    const { targeting } = def;
    if (!targeting) return true;

    if (targeting.url_match) {
      const url = ctx.url ?? '';
      if (!url.toLowerCase().includes(targeting.url_match.toLowerCase())) return false;
    }

    if (targeting.feature_flag) {
      const raw = ctx.feature_flag_values?.[targeting.feature_flag];
      // Truthiness matches `FlagValue::as_bool` via wasm_api.rs:849-853, so a
      // variant string counts as ON and the literal "false" does not.
      const truthy = typeof raw === 'string' ? raw.length > 0 && raw !== 'false' : raw === true;
      if (!truthy) return false;
    }

    const personProperties = ctx.person_properties ?? {};
    for (const condition of targeting.person_properties ?? []) {
      if (!evaluateCondition(condition, personProperties)) return false;
    }

    if (targeting.event_history) {
      const eh = targeting.event_history;
      // Caller-supplied counts win over the engine's own counter (surveys.rs:557-563).
      const supplied = ctx.event_counts?.[eh.event_name];
      const count =
        typeof supplied === 'number' ? supplied : (this.eventCounts.get(eh.event_name) ?? 0);
      if (!countOpMatches(eh.count_op ?? 'gte', count, eh.count ?? 1)) return false;
    }

    if (targeting.frequency_cap) {
      if (!this.withinFrequencyCap(def.id, targeting.frequency_cap)) return false;
    }

    return true;
  }

  /** Port of `is_within_frequency_cap` (surveys.rs:578-588). */
  private withinFrequencyCap(surveyId: string, cap: { max_shows: number; days: number }): boolean {
    const history = this.showHistoryMap.get(surveyId);
    if (!history) return true;
    const cutoff = Math.max(0, Date.now() - cap.days * 24 * 3600 * 1000);
    const recent = history.filter((t) => t >= cutoff).length;
    return recent < cap.max_shows;
  }
}
